import { useEffect, useMemo, useState } from "react";
import { Cloud, ExternalLink, GitPullRequest } from "lucide-react";
import {
  STACK_TIERS,
  estimateStackCost,
  teardownConfirmation,
  type CloudConnectionSummary,
  type CloudProvider,
  type StackCostEstimate,
  type StackTier,
} from "@mycompbox/shared";
import {
  useCloudStatus,
  useProjectCloud,
  useProjectDeployments,
  useProjectStack,
  useSetProjectCloud,
  useSetProjectStack,
  useStackTeardown,
} from "@/hooks/queries/use-cloud";
import { Input } from "@/components/ui/input";
import { apiErrorInfo } from "@/components/pull-requests/pull-request-status";
import { showToast } from "@/hooks/useToast";
import { isEntitlementError } from "@/lib/api/billing";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConnectGitHubGate } from "@/components/github/connect-github-gate";
import { useGitHubStatus, useProjectRepository } from "@/hooks/queries/use-github";
import { projectGitHubReturnTo } from "@/lib/github-return-to";
import { ProjectDeployments } from "./project-deployments";
import { cn } from "@/lib/utils";

const PROVIDER_NAMES: Record<CloudProvider, string> = { gcp: "Google Cloud", aws: "AWS", azure: "Microsoft Azure" };

/** The "Organization default" option's value: a native select can't hold null. */
const DEFAULT_VALUE = "";

/** The server's code for a dev-only switch refused because stage or prod has been deployed. */
const STACK_DOWNGRADE_BLOCKED = "STACK_DOWNGRADE_BLOCKED";

/** How long to keep checking on a teardown before leaving it to the user. */
const TEARDOWN_WATCH_MS = 20 * 60 * 1000;

const STATUS: Record<string, string> = {
  pr_open: "Waiting for the pull request to be merged",
  merged: "Live: every push to the main branch deploys to dev",
  closed: "The pull request was closed without merging",
};

/**
 * The project's stack: which environments it deploys to. Choosing one opens a
 * pull request in the project's repository with the Terraform and the deploy
 * workflow; nothing changes in the cloud until it's merged.
 */
export function ProjectStackCard({ teamId, canEdit }: { teamId: string; canEdit: boolean }) {
  // The environment being torn down, while we wait for it to stop blocking the downgrade.
  const [tearingDown, setTearingDown] = useState<{ environment: string; url: string } | null>(null);
  const watching = tearingDown !== null;
  const { data: stack, isLoading, refetch: refetchStack } = useProjectStack(teamId, watching);
  // Listing the deploys is what notices a finished teardown on the server, so poll it alongside the stack.
  useProjectDeployments(teamId, watching, watching);
  const setStack = useSetProjectStack(teamId);
  const [choice, setChoice] = useState<StackTier | null>(null);
  const [confirmingDowngrade, setConfirmingDowngrade] = useState(false);
  const [teardownFor, setTeardownFor] = useState<string | null>(null);
  const { data: projectCloud } = useProjectCloud(teamId);
  const estimates = useStackEstimates(projectCloud?.cloud ?? null);

  // Done once the environment no longer blocks the downgrade.
  const tornDownEnv =
    tearingDown && stack && !stack.blockedBy.includes(tearingDown.environment) ? tearingDown.environment : null;
  useEffect(() => {
    if (!tornDownEnv) return;
    showToast(`${tornDownEnv} is torn down.`);
    setTearingDown(null);
  }, [tornDownEnv]);
  // Stop checking after a while (e.g. the run failed); the user can follow the run on GitHub.
  useEffect(() => {
    if (!tearingDown) return;
    const timer = setTimeout(() => { setTearingDown(null); }, TEARDOWN_WATCH_MS);
    return () => { clearTimeout(timer); };
  }, [tearingDown]);
  const needsGitHub = !!stack?.needs.includes("github");
  // Same key as the repository card's status, so this shares its request.
  const { data: github } = useGitHubStatus(projectGitHubReturnTo(teamId), needsGitHub);
  const { data: projectRepo } = useProjectRepository(teamId, needsGitHub);
  const githubReady = !!github?.configured && github.installations.some((i) => !i.suspended);

  if (isLoading || !stack) return null;
  const selected = choice ?? stack.tier ?? "dev";
  const downgrading = stack.tier === "full" && selected === "dev";
  const downgradeBlocked = downgrading && !stack.canDowngrade;
  const blockedError = apiErrorInfo(setStack.error).code === STACK_DOWNGRADE_BLOCKED;

  const submit = (tier: StackTier) => {
    setStack.mutate(tier, {
      onSettled: () => { setConfirmingDowngrade(false); },
      onError: (error) => {
        // Someone deployed stage or prod since we loaded: refresh, and the blocked state explains it.
        if (apiErrorInfo(error).code === STACK_DOWNGRADE_BLOCKED) void refetchStack();
      },
    });
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-medium">Stack</h3>
        <p className="text-sm text-muted-foreground">
          Where this project deploys. CompBox writes the infrastructure and the deploy workflow into the repository as a
          pull request, and keeps them up to date.
        </p>
      </div>

      <DeploysTo teamId={teamId} canEdit={canEdit} merged={stack.status === "merged"} />

      {stack.needs.includes("github") ? (
        !github || (github.configured && !githubReady && !projectRepo) ? null : githubReady ? (
          <p className="text-sm text-muted-foreground">
            Pick this project's repository from GitHub above (Repository → Change) to set up the stack.
          </p>
        ) : github.configured && projectRepo?.github ? (
          // Linked through GitHub, but no usable installation: the app is suspended. The
          // repository card shows no Connect step in this case, so don't point at one.
          <p className="text-sm text-muted-foreground">
            The CompBox app is suspended on GitHub. A GitHub admin can unsuspend it.
          </p>
        ) : github.configured ? (
          // The repository card above already shows the Connect GitHub step; don't stack a second one.
          <p className="text-sm text-muted-foreground">Connect GitHub in the Repository section above.</p>
        ) : (
          <ConnectGitHubGate
            returnTo={projectGitHubReturnTo(teamId)}
            teamId={teamId}
            reason="so CompBox can open the stack's pull request and deploy"
          />
        )
      ) : stack.needs.length > 0 ? (
        <p className="text-sm text-muted-foreground">
          Your organization admin needs to connect a cloud first (Settings → Cloud).
        </p>
      ) : (
        <>
          {stack.tier ? (
            <div className="flex flex-col gap-2 rounded-lg border border-white/[0.08] p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <span className="font-medium">{STACK_TIERS[stack.tier].label}</span>
                <span className="ml-2 text-xs text-muted-foreground">{stack.status ? STATUS[stack.status] : ""}</span>
              </div>
              {stack.prUrl ? (
                <Button asChild size="sm" variant="outline">
                  <a href={stack.prUrl} target="_blank" rel="noreferrer">
                    <GitPullRequest className="mr-1 h-3.5 w-3.5" />
                    Pull request
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </Button>
              ) : null}
            </div>
          ) : null}

          {stack.status === "merged" && projectCloud?.cloud?.liveUrl ? (
            <p className="text-sm">
              <span className="text-muted-foreground">Live at </span>
              <a href={projectCloud.cloud.liveUrl} target="_blank" rel="noreferrer" className="underline">
                {projectCloud.cloud.liveUrl.replace(/^https?:\/\//, "")}
              </a>
            </p>
          ) : null}

          {stack.status === "merged" ? <ProjectDeployments teamId={teamId} isAdmin={canEdit} /> : null}

          {canEdit ? (
            <div className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Stack">
                {(Object.keys(STACK_TIERS) as StackTier[]).map((tier) => (
                  <div key={tier} className="space-y-2">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected === tier}
                    onClick={() => { setChoice(tier); }}
                    className={cn(
                      "rounded-lg border p-3 text-left text-sm",
                      selected === tier ? "border-primary bg-primary/10" : "border-white/[0.08]"
                    )}
                  >
                    <div className="font-medium">{STACK_TIERS[tier].label}</div>
                    <div className="text-xs text-muted-foreground">
                      {tier === "dev" ? "One environment, deployed on every push." : "Dev on every push; stage and prod on request, prod with approval."}
                    </div>
                  </button>
                  {estimates?.[tier] ? <CostEstimate estimate={estimates[tier]} /> : null}
                  </div>
                ))}
              </div>
              {downgradeBlocked ? (
                <DowngradeBlocked
                  blockedBy={stack.blockedBy}
                  tearingDown={tearingDown}
                  onTeardown={(env) => { setTeardownFor(env); }}
                />
              ) : setStack.error && !blockedError ? (
                <p className="text-sm text-destructive">{setStack.error.message}</p>
              ) : null}
              <Button
                disabled={setStack.isPending || downgradeBlocked || (selected === stack.tier && stack.status === "merged")}
                onClick={() => {
                  if (downgrading) setConfirmingDowngrade(true);
                  else submit(selected);
                }}
              >
                {setStack.isPending ? "Opening pull request…" : stack.tier ? "Update the pull request" : "Open the pull request"}
              </Button>

              <Dialog
                open={confirmingDowngrade}
                onOpenChange={(open) => { if (!open && !setStack.isPending) setConfirmingDowngrade(false); }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Switch to dev only?</DialogTitle>
                    <DialogDescription>
                      The pull request removes stage and prod from the deploy workflow, along with the teardown workflow
                      and their environment files. Stage and prod were never deployed, so nothing in the cloud changes.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" disabled={setStack.isPending} onClick={() => { setConfirmingDowngrade(false); }}>
                      Cancel
                    </Button>
                    <Button disabled={setStack.isPending} onClick={() => { submit("dev"); }}>
                      {setStack.isPending ? "Opening pull request…" : "Switch to dev only"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {stack.app && stack.blockedBy.length > 0 ? (
                <TeardownDialog
                  teamId={teamId}
                  app={stack.app}
                  environments={stack.blockedBy}
                  initial={teardownFor}
                  onClose={() => { setTeardownFor(null); }}
                  onStarted={(started) => {
                    setTeardownFor(null);
                    setTearingDown(started);
                  }}
                />
              ) : null}
            </div>
          ) : !stack.tier ? (
            <p className="text-sm text-muted-foreground">Ask a project admin to set up the stack.</p>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Each tier's monthly cost on the project's cloud, where we have prices for it (Google Cloud for now). */
function useStackEstimates(cloud: CloudConnectionSummary | null): Partial<Record<StackTier, StackCostEstimate>> | null {
  const provider = cloud?.provider;
  const region = cloud?.region;
  return useMemo(() => {
    if (provider !== "gcp" || !region) return null;
    const out: Partial<Record<StackTier, StackCostEstimate>> = {};
    for (const tier of Object.keys(STACK_TIERS) as StackTier[]) {
      const estimate = estimateStackCost(tier, { provider, region });
      if (estimate) out[tier] = estimate;
    }
    return out;
  }, [provider, region]);
}

function dollars(n: number): string {
  return `$${n.toLocaleString("en-US")}`;
}

function CostEstimate({ estimate }: { estimate: StackCostEstimate }) {
  return (
    <div className="rounded-lg border border-white/[0.08] p-3 text-xs text-muted-foreground" data-testid="stack-cost">
      <div>
        Estimated Google Cloud cost:{" "}
        <span className="font-medium text-foreground">
          {dollars(estimate.total.low)}–{dollars(estimate.total.high)} / month
        </span>
      </div>
      <div>
        Prices as of {estimate.pricesAsOf}. An estimate from list prices, not a quote: your bill depends on traffic.{" "}
        <a href={estimate.calculatorUrl} target="_blank" rel="noopener noreferrer" className="underline">
          Google Cloud pricing calculator
        </a>
      </div>
      <details className="mt-1">
        <summary className="cursor-pointer">How this is estimated</summary>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          {estimate.assumptions.map((a) => <li key={a}>{a}</li>)}
        </ul>
      </details>
    </div>
  );
}

/** Why the project can't switch to dev only, and the way out: tear down what was deployed. */
function DowngradeBlocked({
  blockedBy,
  tearingDown,
  onTeardown,
}: {
  blockedBy: string[];
  tearingDown: { environment: string; url: string } | null;
  onTeardown: (environment: string) => void;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-amber-500/40 p-3 text-sm" role="status">
      <p>
        This project has already deployed to {blockedBy.join(" and ")}, so it can't switch to dev only. Tear{" "}
        {blockedBy.length > 1 ? "them" : "it"} down first.
      </p>
      {tearingDown ? (
        <p className="text-muted-foreground">
          Tearing down {tearingDown.environment}… This updates when it's done.{" "}
          <a href={tearingDown.url} target="_blank" rel="noreferrer" className="underline">
            Watch the run
          </a>
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {blockedBy.map((env) => (
            <Button key={env} size="sm" variant="destructive" onClick={() => { onTeardown(env); }}>
              Tear down {env}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Tears down stage or prod, once the user types "<app>-<environment>". */
function TeardownDialog({
  teamId,
  app,
  environments,
  initial,
  onClose,
  onStarted,
}: {
  teamId: string;
  app: string;
  environments: string[];
  /** The environment to start with; the dialog is open while this is set. */
  initial: string | null;
  onClose: () => void;
  onStarted: (started: { environment: string; url: string }) => void;
}) {
  const teardown = useStackTeardown(teamId);
  const [environment, setEnvironment] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const env = environment ?? initial ?? environments[0] ?? "";
  const expected = teardownConfirmation(app, env);

  const reset = () => {
    setEnvironment(null);
    setTyped("");
    teardown.reset();
  };

  const close = () => {
    if (teardown.isPending) return;
    reset();
    onClose();
  };

  const start = () => {
    teardown.mutate(
      { environment: env, confirm: typed },
      {
        onSuccess: (res) => {
          reset();
          onStarted({ environment: res.data.environment, url: res.data.url });
        },
      }
    );
  };

  return (
    <Dialog open={initial !== null} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tear down {env}?</DialogTitle>
          <DialogDescription>
            This deletes {env}'s Cloud Run service and the rest of its Terraform-managed infrastructure. The app stops
            serving in {env}. The code and the other environments aren't touched.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {environments.length > 1 ? (
            <div className="flex items-center gap-2">
              <label htmlFor={`teardown-env-${teamId}`} className="font-medium">
                Environment
              </label>
              <select
                id={`teardown-env-${teamId}`}
                className="h-8 rounded-md border border-white/[0.08] bg-background px-2 text-sm"
                value={env}
                disabled={teardown.isPending}
                onChange={(e) => {
                  setEnvironment(e.target.value);
                  setTyped("");
                }}
              >
                {environments.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <label htmlFor={`teardown-confirm-${teamId}`} className="block">
            Type <span className="font-mono font-medium">{expected}</span> to confirm
          </label>
          <Input
            id={`teardown-confirm-${teamId}`}
            value={typed}
            autoComplete="off"
            disabled={teardown.isPending}
            onChange={(e) => { setTyped(e.target.value); }}
          />
          {teardown.error ? <p className="text-destructive">{teardown.error.message}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={teardown.isPending} onClick={close}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={teardown.isPending || typed !== expected} onClick={start}>
            {teardown.isPending ? "Starting teardown…" : `Tear down ${env}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Which of the organization's clouds the project deploys to. "Organization default" follows
 * whichever connection is the default; picking one pins the project to it. Changing the cloud
 * of a merged stack asks first, because the next deploy goes to the new cloud and nothing is
 * moved out of the old one.
 */
function DeploysTo({ teamId, canEdit, merged }: { teamId: string; canEdit: boolean; merged: boolean }) {
  const { data: status } = useCloudStatus();
  const { data: project } = useProjectCloud(teamId);
  const setCloud = useSetProjectCloud(teamId);
  const [pending, setPending] = useState<string | null | undefined>(undefined);

  const connections = status?.connections ?? [];
  // No connections: the stack's own "connect a cloud first" message covers it.
  if (!status || !project || connections.length === 0) return null;

  const fallback = connections.find((c) => c.isDefault) ?? null;
  const current = project.cloud;
  const value = project.isInherited ? DEFAULT_VALUE : (project.cloudConnectionId ?? DEFAULT_VALUE);
  const resolve = (id: string | null) => (id === null ? fallback : (connections.find((c) => c.id === id) ?? null));
  const target = pending === undefined ? null : resolve(pending);

  const save = (id: string | null) => {
    setCloud.mutate(id, {
      onSuccess: (res) => {
        setPending(undefined);
        if (res.stackWillRetarget && res.cloud) showToast(`The next deploy will go to ${res.cloud.name}.`);
      },
      onError: (error) => {
        setPending(undefined);
        // The app-wide handler already explains read-only organizations.
        if (!isEntitlementError(error)) showToast(`Couldn't change the cloud: ${error.message}`, 6000);
      },
    });
  };

  const choose = (raw: string) => {
    const id = raw === DEFAULT_VALUE ? null : raw;
    if (raw === value) return;
    // Only ask when the deploy target actually moves on a stack that's already deploying.
    if (merged && resolve(id)?.id !== current?.id) {
      setPending(id);
      return;
    }
    save(id);
  };

  const label = current ? `${current.name}${project.isInherited ? " (default)" : ""}` : "No cloud";

  return (
    <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-3">
      <label htmlFor={`deploys-to-${teamId}`} className="font-medium">
        Deploys to
      </label>
      {canEdit ? (
        <div className="flex items-center gap-2">
          {current ? <ProviderIcon provider={current.provider} /> : null}
          <select
            id={`deploys-to-${teamId}`}
            className="h-8 rounded-md border border-white/[0.08] bg-background px-2 text-sm"
            value={value}
            disabled={setCloud.isPending}
            onChange={(e) => { choose(e.target.value); }}
          >
            <option value={DEFAULT_VALUE}>Organization default{fallback ? ` (${fallback.name})` : ""}</option>
            {connections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {PROVIDER_NAMES[c.provider]}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <span id={`deploys-to-${teamId}`} className="flex items-center gap-2 text-muted-foreground">
          {current ? <ProviderIcon provider={current.provider} /> : null}
          {label}
        </span>
      )}

      <Dialog open={pending !== undefined} onOpenChange={(open) => { if (!open && !setCloud.isPending) setPending(undefined); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deploy to {target?.name ?? "another cloud"}?</DialogTitle>
            <DialogDescription>
              The next deploy will target {target?.name ?? "the new cloud"}. Existing infrastructure in{" "}
              {current?.name ?? "the current cloud"} is not moved or deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={setCloud.isPending} onClick={() => { setPending(undefined); }}>
              Cancel
            </Button>
            <Button disabled={setCloud.isPending} onClick={() => { save(pending ?? null); }}>
              {setCloud.isPending ? "Saving…" : "Change cloud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProviderIcon({ provider }: { provider: CloudProvider }) {
  return (
    <span title={PROVIDER_NAMES[provider]} aria-label={PROVIDER_NAMES[provider]} className="inline-flex">
      <Cloud className="h-3.5 w-3.5 text-muted-foreground" />
    </span>
  );
}
